// src/lib/channels/ical.js — stack iCal (parser + fetch + import).
//
// Pourquoi lib/channels/ : iCal est un protocole d'un canal de distribution
// (Airbnb / Booking / Superhote l'utilisent). Préfigure les futurs adaptateurs
// canal manager (Beds24, Airbnb API native, Booking API, etc.).
//
// Lecture côté client via /api/ical-proxy (whitelist de domaines + bypass CORS).
// Voir api/ical-proxy.js.

import { uid } from '../format'

// Mots-clés indiquant un blocage Airbnb (pas une vraie réservation).
// NB : "not available" volontairement EXCLU — c'est un masquage de nom Airbnb
// sur les résas futures (équivalent de "Reserved"), pas un blocage proprio.
export const BLOCK_KEYWORDS = ['indisponible', 'unavailable', 'blocked', 'blockoff', 'closed']

// Helper : retourne (ou initialise) la structure tombstones globale pour un
// logement donné. Structure stockée à `data._tombstones[lid]` (M-3 : namespace
// global par logement, pas par mois — évite fuite cross-mois P4).
//
// Quatre buckets :
//   - ical_uids[]       : UIDs iCal supprimés (path principal)
//   - legacy_checkins[] : dates checkIn supprimées sans UID (filet B-1)
//   - airbnb_codes[]    : codes Airbnb supprimés (CSV Airbnb B-3)
//   - booking_refs[]    : refs Booking supprimés (CSV Booking B-3)
//
// Fallback rétrocompat : si `data._tombstones` absent → renvoie objet vide.
// Mutation safe : la fonction ne mute PAS `data` (read-only). L'initialisation
// effective doit être faite par l'appelant qui clone le blob (deep clone).
export function getTombstones(data, lid) {
  const empty = { ical_uids: [], legacy_checkins: [], airbnb_codes: [], booking_refs: [] }
  const tomb = data?._tombstones?.[lid]
  if (!tomb) return empty
  return {
    ical_uids: Array.isArray(tomb.ical_uids) ? tomb.ical_uids : [],
    legacy_checkins: Array.isArray(tomb.legacy_checkins) ? tomb.legacy_checkins : [],
    airbnb_codes: Array.isArray(tomb.airbnb_codes) ? tomb.airbnb_codes : [],
    booking_refs: Array.isArray(tomb.booking_refs) ? tomb.booking_refs : [],
  }
}

// Parse un flux iCal brut → array d'événements
// { debut, fin, voyageur, source, isBlock, icalUid, summary, url }.
//
// `icalUid` exposé en sortie (champ UID du VEVENT, RFC 5545). null si absent.
// Utilisé en aval par `importIcalForLog` pour matcher les résas par identifiant
// stable plutôt que par date seule (cf. plan fix iCal 2026-05-23).
//
// `summary` et `url` bruts exposés en sortie (ADR-179) : utilisés par
// `extractSuperhoteId` pour récupérer le numéro interne Superhote depuis le
// SUMMARY (`'Pierre - Airbnb.com - 9999001'`) ou l'URL
// (`https://app.superhote.com/bookings/9999001`). Le numéro Superhote est
// stable entre cycles iCal alors que l'UID `…@app.superhote.com` change à
// chaque fetch.
export function parseIcalText(text) {
  const events = []
  // Unfold les lignes (RFC 5545 : continuation lines avec CRLF + espace)
  const unfolded = text.replace(/\r\n[ \t]/g, '').replace(/\n[ \t]/g, '')
  const blocks = unfolded.split('BEGIN:VEVENT')
  blocks.slice(1).forEach(block => {
    const get = (key) => {
      const m = block.match(new RegExp(key + '[^:]*:([^\r\n]+)'))
      return m ? m[1].trim() : ''
    }
    const dtstart = get('DTSTART')
    const dtend = get('DTEND')
    const summary = get('SUMMARY')
    const status = get('STATUS')
    const rawUid = get('UID')
    const icalUid = rawUid && rawUid.length > 0 ? rawUid : null
    const url = get('URL')
    if (dtstart && dtend && status !== 'CANCELLED') {
      const parseDate = s => {
        if (s.length === 8) return new Date(s.slice(0,4)+'-'+s.slice(4,6)+'-'+s.slice(6,8))
        return new Date(s)
      }
      const summaryLow = summary.toLowerCase()
      const isBlock = BLOCK_KEYWORDS.some(k => summaryLow.includes(k))
      const voyageur = summary.replace(/^(Airbnb|Booking\.com|BLOCKOFF|CLOSED|Réservé|Reserved)/i,'').trim() || (isBlock ? '' : summary)
      const source = summaryLow.includes('airbnb') ? 'airbnb' : summaryLow.includes('booking') ? 'booking' : 'direct'
      events.push({
        debut: parseDate(dtstart),
        fin: parseDate(dtend),
        voyageur,
        source,
        isBlock,
        icalUid,
        summary,
        url,
      })
    }
  })
  return events.filter(e => e.debut && e.fin && !isNaN(e.debut))
}

// Extrait le numéro interne Superhote depuis un événement iCal parsé (ADR-179).
//
// Les UIDs Superhote (`<hash>-<timestamp>@app.superhote.com`) changent à chaque
// fetch iCal alors que le numéro interne reste stable. Ce numéro est exposé :
//   - dans le SUMMARY : `'Pierre - Airbnb.com - 9999001'`
//   - dans l'URL : `https://app.superhote.com/bookings/9999001`
//
// Retourne le numéro (string) ou null :
//   - null si l'UID ne ressemble pas à un domaine Superhote (guard strict)
//   - null si extraction impossible malgré UID Superhote-like (warn défensif)
//
// C-2 : regex assouplie `/@(\w+\.)?superhote\.com$/i` pour couvrir
// `@app.superhote.com`, `@staging.superhote.com`, `@legacy.superhote.com`,
// etc., sans rupture sur évolution sous-domaine non annoncée.
export function extractSuperhoteId(event) {
  const uid = (event && event.icalUid) || ''

  // Détection préalable : UID doit ressembler à un domaine Superhote (guard
  // strict — empêche faux positifs sur SUMMARY pathologique avec UID non
  // Superhote, cf. T-PS3 et T-PS11 variante A).
  const looksLikeSuperhote = /@(\w+\.)?superhote\.com$/i.test(uid)
  if (!looksLikeSuperhote) return null

  // Essai SUMMARY : capture le numéro en fin de titre.
  const sumMatch = (event.summary || '').match(/-\s+(\d+)\s*$/)
  if (sumMatch) return sumMatch[1]

  // Essai URL : capture l'identifiant dans l'URL Superhote.
  const urlMatch = (event.url || '').match(/bookings\/(\d+)/)
  if (urlMatch) return urlMatch[1]

  // C-2 : UID Superhote-like sans extraction possible → warn défensif.
  // Aide à diagnostiquer un changement de format flux Superhote non
  // annoncé (signal faible, pas une erreur bloquante).
  // Q-1 RGPD : pas de PII dans les logs — le SUMMARY contient souvent le
  // prénom du voyageur. On signale uniquement la présence/absence des
  // champs, pas leur contenu.
  console.warn('[iCal sync] UID Superhote-like mais extraction superhote_id échouée — vérifier format flux', {
    uid_domain: (uid.match(/@.+$/) || [''])[0],
    summary_present: !!event.summary,
    url_present: !!event.url,
  })
  return null
}

// Détecte la plateforme depuis l'URL iCal (fix source "direct" pour Airbnb
// quand le SUMMARY ne contient pas de mention).
export function getSourceFromIcalUrl(url) {
  if (!url) return null
  if (url.includes('airbnb')) return 'airbnb'
  if (url.includes('booking')) return 'booking'
  if (url.includes('superhote')) return null // multi-plateforme, laisser détection SUMMARY
  return null
}

// Fetch via le proxy serveur, parse, retourne les événements. [] si erreur.
export async function fetchIcal(url) {
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 12000)
    const res = await fetch(`/api/ical-proxy?url=${encodeURIComponent(url)}`, { signal: controller.signal })
    clearTimeout(timer)
    if (!res.ok) {
      const body = await res.text().catch(() => '')
      console.error(`iCal proxy error ${res.status} pour ${url} : ${body.slice(0, 200)}`)
      return []
    }
    const text = await res.text()
    return parseIcalText(text)
  } catch (e) {
    console.error('iCal fetch error pour', url, ':', e.message)
    return []
  }
}

// Sentinelles de masquage de noms par les OTA (Airbnb principalement).
const MASQUAGES_VOYAGEUR = ['Reserved', 'BLOCKOFF', '(Not available)', 'Not available']

// ─────────────────────────────────────────────────────────────────────────────
// Matching secondaire cross-source (fix bug frère doublons iCal — ADR-178)
// ─────────────────────────────────────────────────────────────────────────────
//
// Plateformes éligibles au matching secondaire `(checkIn, plateforme)` entre une
// résa CSV existante (sans `ical_uid`) et un événement iCal entrant. La
// whitelist garantit qu'aucune résa `direct` (manuelle ou pricing_block) ne
// peut jamais être touchée par le matching secondaire, même si l'événement
// iCal est résolu sans mention OTA explicite.
export const MATCHABLE_PLATFORMS = new Set(['airbnb', 'booking'])

// Logements sous-loc exclus du matching secondaire. Sur ces chambres
// internes, la structure de données est anormale (résas vides catégorie B,
// éclatement par nuit) — le risque de faux positif est imprévisible. Le
// nettoyage relève d'une étape 4b dédiée. Comportement original conservé :
// si l'UID est inconnu → INSERT fantôme comme avant.
export const SUBLOC_LIDS = new Set(['lc1', 'lc2', 'lc3'])

// Helper interne : tente de matcher un événement iCal entrant (UID inconnu)
// avec une résa CSV existante via la clé composite `(checkIn, plateforme)`.
//
// Retourne la résa candidate (objet du `reservationsClone` muté plus tard)
// ou `null` si aucun match n'est valide.
//
// Garantit qu'aucun champ financier (`montant`, `menage`, `taxe`) ne sera
// touché : seule la pose de `ical_uid` est de la responsabilité de
// l'appelant. Les gardes structurelles sont :
//   - B-1 normalisation case-insensitive des deux côtés (toLowerCase + trim)
//   - B-2 whitelist `MATCHABLE_PLATFORMS` (jamais de fusion sur 'direct')
//   - garde plateforme absente (skip si vide après normalisation)
//   - garde manual_origin (les résas éditées admin sont sanctuarisées)
//   - M-2 défense en profondeur tombstones (résa supprimée → skip)
//   - skip des résas déjà matchées via le path UID (r.ical_uid truthy)
//
// L'argument `reservationsClone` doit être le tableau qui sera sauvegardé
// (M-1 — pas `existing` original, sinon la mutation appelée à l'extérieur
// porterait sur le mauvais objet).
export function findSecondaryMatch(reservationsClone, dateStr, evtPlateforme, tombstones) {
  // B-1 : normalisation case-insensitive côté événement iCal
  const evtPlat = (evtPlateforme || '').toLowerCase().trim()

  // B-2 : whitelist — jamais de matching sur plateforme non-matchable ('direct'…)
  if (!evtPlat) return null
  if (!MATCHABLE_PLATFORMS.has(evtPlat)) return null

  // M-2 défense en profondeur : précalcul des Set tombstones (objets {airbnbCode}
  // / {bookingRef}). Cohérent avec la structure posée par `pushTombstonesForResa`.
  const tombAirbnb = new Set((tombstones?.airbnb_codes || []).map(t => t?.airbnbCode))
  const tombBooking = new Set((tombstones?.booking_refs || []).map(t => t?.bookingRef))

  for (const r of reservationsClone) {
    // Skip résas déjà matchées (le path principal UID les a déjà traitées)
    if (r.ical_uid) continue
    // Garde manual_origin : résa éditée admin → jamais muter via matching secondaire
    if (r.manual_origin) continue

    // B-1 : normalisation case-insensitive côté résa CSV
    const rPlat = (r.plateforme || '').toLowerCase().trim()

    // Garde plateforme absente + B-2 : whitelist côté résa
    if (!rPlat) continue
    if (!MATCHABLE_PLATFORMS.has(rPlat)) continue

    // M-2 garde tombstone : si la résa a un airbnbCode/bookingRef tombstoné,
    // c'est qu'elle a été supprimée volontairement → skip. Filet final en cas
    // de bug cohérence KV laissant une résa orpheline dans `reservationsClone`.
    if (r.airbnbCode && tombAirbnb.has(r.airbnbCode)) continue
    if (r.bookingRef && tombBooking.has(r.bookingRef)) continue

    // Clé composite (checkIn + plateforme normalisés)
    const rCheckIn = r.checkIn || r.date || ''
    if (rCheckIn === dateStr && rPlat === evtPlat) return r
  }
  return null
}

// Helper : vérifie si une facture est déjà émise pour un logement+mois donnés.
// Lecture seule de la KV `fbz8-factlog` (storage.get). Retourne false en cas
// d'erreur ou storage indisponible (best-effort — l'audit M-1 garde toujours
// son `console.warn`, le `console.error` est un sur-signal optionnel).
async function _hasInvoiceForMonth(lid, mk) {
  try {
    // eslint-disable-next-line global-require
    const { storage } = require('../../supabase')
    const dr = await storage.get('fbz8-factlog')
    if (!dr) return false
    const log = JSON.parse(dr.value || '{}')
    // factLog shape : log[lid]?.[mk] = [...] ou log[mk]?.[lid] ; on couvre
    // les deux pour robustesse.
    if (log?.[lid]?.[mk] && Array.isArray(log[lid][mk]) && log[lid][mk].length > 0) return true
    if (log?.[mk]?.[lid] && Array.isArray(log[mk][lid]) && log[mk][lid].length > 0) return true
    return false
  } catch (_e) {
    return false
  }
}

// Import iCal pour un logement et un mois donnés.
//
// Algo dual-path post-fix 2026-05-23 (cf. plan-fix-ical-respect-modifications-manuelles.md) :
//   - PATH UID  : si l'événement a un icalUid, match par UID dans les résas
//                 existantes. Skip si UID dans tombstones. Mise à jour
//                 silencieuse des champs iCal si !manual_origin.
//   - PATH LEGACY (sans UID) : fallback dédup par checkIn comme avant.
//                 Filet B-1 : skip si checkIn dans tombstones legacy.
//
// Mises à jour silencieuses respectent strictement `manual_origin`. Audit trail
// M-1 (`ical_sync_log` + console.warn) sur toute modification de champ.
//
// Retourne le nombre de résas ajoutées (les mises à jour ne sont PAS comptées).
export async function importIcalForLog(log, month, y, m, data, saveData) {
  try {
    const events = await fetchIcal(log.ical)
    // Fix timezone : comparaison en chaîne ISO pour éviter décalage UTC/local
    const mStart = `${y}-${String(m).padStart(2,'0')}-01`
    const lastDay = new Date(y, m, 0).getDate()
    const mEnd = `${y}-${String(m).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`
    const sourceOverride = getSourceFromIcalUrl(log.ical)
    const filtered = events.filter(e => {
      if (e.isBlock) return false
      const dateStr = e.debut.toISOString().split('T')[0]
      return dateStr >= mStart && dateStr <= mEnd
    })
    if (!filtered.length) return 0

    const mk = month
    const lid = log.id
    const existing = data?.[mk]?.[lid]?.reservations || []
    const tombstones = getTombstones(data, lid)

    // Index UID : seules les résas avec ical_uid non-null sont indexées.
    const existByUid = new Map()
    existing.forEach(r => {
      if (r.ical_uid) existByUid.set(r.ical_uid, r)
    })
    // Index legacy : seules les résas sans ical_uid ET non-manuelles sont
    // dédupliquées par date. Une résa manuelle (manual_origin=true) à la même
    // date ne bloque pas une insertion iCal — cas P3 résolu (M-4 warn dans saveResa).
    const existDateLegacy = new Set(
      existing
        .filter(r => !r.ical_uid && !r.manual_origin)
        .map(r => r.checkIn || r.date || '')
    )

    const deletedUidSet = new Set(tombstones.ical_uids.map(t => t.uid))
    const deletedLegacyDateSet = new Set(tombstones.legacy_checkins.map(t => t.checkIn))

    let added = 0
    let updatedCount = 0
    // Clone profond pour pouvoir muter les résas existantes (mise à jour M-1).
    const reservationsClone = JSON.parse(JSON.stringify(existing))
    // Re-index sur le clone pour pointer vers les bons objets.
    const cloneByUid = new Map()
    reservationsClone.forEach(r => { if (r.ical_uid) cloneByUid.set(r.ical_uid, r) })

    // Index superhote_id → résa (ADR-179 pansement Superhote). Sous-loc exclus
    // (structure anormale). Le numéro interne Superhote est stable entre cycles
    // alors que l'UID `…@app.superhote.com` change à chaque fetch.
    const cloneBySuperhoteId = new Map()
    if (!SUBLOC_LIDS.has(lid)) {
      reservationsClone.forEach(r => {
        if (r.superhote_id) cloneBySuperhoteId.set(r.superhote_id, r)
      })
    }

    const auditErrors = []  // [{lid, mk, uid, field, old, new}] pour console.error post-save

    for (const e of filtered) {
      const dateStr = e.debut.toISOString().split('T')[0]
      const nuits = Math.round((e.fin - e.debut) / (1000 * 60 * 60 * 24))
      const voyageurResolved = MASQUAGES_VOYAGEUR.includes(e.voyageur) ? '' : (e.voyageur || '')
      const uidEvent = e.icalUid

      // ── PATH SUPERHOTE_ID (ADR-179, exclu sous-loc) ──
      // Pré-check stable : si l'événement a un superhote_id et qu'une résa
      // existante porte le même superhote_id, on enrichit / rafraîchit sans
      // créer de fantôme. Ferme le robinet des doublons quand l'UID Superhote
      // change entre cycles.
      //
      // NOTE : numéros Superhote semblent séquentiels (observés 7827090 <
      // 7931035 < 7985481 < 8027546 …) → suggère auto-incrément global, non
      // réutilisé. Risque résiduel : recyclage post-purge longue durée non
      // documenté Superhote. Garde anti-fusion checkIn ci-dessous atténue ce
      // risque pour les cas où le numéro serait réattribué à un séjour à une
      // date différente. (C-5)
      const superhoteId = !SUBLOC_LIDS.has(lid) ? extractSuperhoteId(e) : null
      // Q-2 : si fall-through après garde anti-fusion, le fantôme inséré ne
      // doit PAS hériter du même superhote_id que la résa originale (sinon
      // 2 résas distinctes avec même superhote_id en base → état incohérent).
      let fellThroughShIdGuard = false
      if (superhoteId) {
        const matchedByShId = cloneBySuperhoteId.get(superhoteId)
        if (matchedByShId) {
          const matchedCheckIn = matchedByShId.checkIn || matchedByShId.date || ''
          if (matchedCheckIn !== dateStr) {
            // C-1 GARDE ANTI-FUSION : EXCLUSIVE au path superhote_id.
            // Le superhote_id correspond mais les dates divergent. Deux cas
            // possibles : (a) recyclage improbable du numéro Superhote sur une
            // résa différente (cf. C-5 ci-dessus) ; (b) déplacement de date
            // déjà géré par un autre canal. Dans les deux cas on NE FUSIONNE
            // PAS ici. Chute intentionnelle vers le path UID.
            console.warn('[iCal sync] superhote_id match mais checkIn divergent — fusion refusée, path UID prend le relais', {
              lid, mk, superhoteId, matchedCheckIn, eventCheckIn: dateStr,
            })
            fellThroughShIdGuard = true
            // FALL-THROUGH vers le path UID
          } else if (matchedByShId.manual_origin) {
            // Résa éditée admin : sanctuarisée, skip total
            continue
          } else {
            // Match légitime : même superhote_id + même checkIn.
            // Rafraîchir l'UID éphémère dans l'index (cohérence cross-cycles)
            // et poser superhote_id si absent (résas CSV pures).
            let didChange = false
            const nowIso = new Date().toISOString()
            if (uidEvent && matchedByShId.ical_uid !== uidEvent) {
              if (!Array.isArray(matchedByShId.ical_sync_log)) matchedByShId.ical_sync_log = []
              matchedByShId.ical_sync_log.push({
                at: nowIso,
                field: 'ical_uid',
                matched_via: 'superhote_id',
                old: matchedByShId.ical_uid || null,
                new: uidEvent,
                superhoteId,
              })
              // Désindexer l'ancien UID si présent puis ré-indexer le nouveau
              if (matchedByShId.ical_uid) cloneByUid.delete(matchedByShId.ical_uid)
              matchedByShId.ical_uid = uidEvent
              cloneByUid.set(uidEvent, matchedByShId)
              didChange = true
            }
            if (!matchedByShId.superhote_id) {
              matchedByShId.superhote_id = superhoteId
              didChange = true
            }
            if (didChange) {
              console.log('[iCal sync] résa rafraîchie via superhote_id (UID éphémère mis à jour)', {
                lid, mk, dateStr, superhoteId, uid: uidEvent,
              })
              updatedCount++
            }
            continue  // Skip INSERT — robinet fermé
          }
        }
      }

      // ── PATH UID ──
      if (uidEvent) {
        // Skip si UID supprimé volontairement par admin (tombstone)
        if (deletedUidSet.has(uidEvent)) continue

        const matched = cloneByUid.get(uidEvent)
        if (matched) {
          // Pas de modification si manual_origin=true (admin a édité)
          if (matched.manual_origin) continue

          // Mise à jour silencieuse des champs iCal (audit M-1 sur chaque modif)
          const nowIso = new Date().toISOString()
          let didChange = false

          // voyageur : maj si actuel vide ou sentinelle, et nouveau pas une sentinelle
          const currentVoyageur = matched.voyageur || ''
          const currentIsSentinel = MASQUAGES_VOYAGEUR.includes(currentVoyageur) || currentVoyageur === ''
          const newIsSentinel = MASQUAGES_VOYAGEUR.includes(e.voyageur) || !e.voyageur
          if (currentIsSentinel && !newIsSentinel && voyageurResolved !== currentVoyageur) {
            if (!Array.isArray(matched.ical_sync_log)) matched.ical_sync_log = []
            // Q-1 RGPD : ne pas logger les noms voyageurs en clair (PII).
            // L'ical_sync_log stocke encore old/new pour audit local côté résa.
            console.warn('[iCal sync] voyageur updated', {
              lid, mk, uid: uidEvent,
              old: currentVoyageur ? '[présent]' : '[vide]',
              new: voyageurResolved ? '[présent]' : '[vide]',
            })
            matched.ical_sync_log.push({ at: nowIso, field: 'voyageur', old: currentVoyageur, new: voyageurResolved })
            matched.voyageur = voyageurResolved
            didChange = true
          }

          // nuits : maj si nouvelle valeur différente (M-2 : manual_origin seul garde)
          const newNuits = nuits > 0 ? nuits : 1
          if (newNuits !== matched.nuits) {
            if (!Array.isArray(matched.ical_sync_log)) matched.ical_sync_log = []
            console.warn('[iCal sync] nuits updated', { lid, mk, uid: uidEvent, old: matched.nuits, new: newNuits })
            matched.ical_sync_log.push({ at: nowIso, field: 'nuits', old: matched.nuits, new: newNuits })
            matched.nuits = newNuits
            didChange = true
          }

          // checkIn : maj si date a changé (déplacement Airbnb)
          if ((matched.checkIn || matched.date) !== dateStr) {
            if (!Array.isArray(matched.ical_sync_log)) matched.ical_sync_log = []
            const oldCheckIn = matched.checkIn || matched.date || null
            console.warn('[iCal sync] checkIn updated for UID', uidEvent, ':', oldCheckIn, '→', dateStr, 'logement:', lid)
            matched.ical_sync_log.push({ at: nowIso, field: 'checkIn', old: oldCheckIn, new: dateStr })
            matched.checkIn = dateStr
            matched.date = dateStr
            didChange = true
            // Audit comptable : si facture émise sur l'ancien mois, logger en error
            const oldMk = (oldCheckIn || '').slice(0, 7)
            if (oldMk && oldMk !== mk) {
              auditErrors.push({ lid, oldMk, newMk: mk, uid: uidEvent, oldCheckIn, newCheckIn: dateStr })
            }
          }

          if (didChange) updatedCount++
          continue
        }

        // UID inconnu → tentative de matching secondaire (checkIn, plateforme)
        // AVANT d'insérer un fantôme. Cf. ADR-178. Exclu pour les sous-loc
        // (SUBLOC_LIDS) où la structure de données est anormale.
        const isSubLoc = SUBLOC_LIDS.has(lid)
        if (!isSubLoc) {
          const resolvedPlateforme = sourceOverride || e.source
          // M-1 : passer reservationsClone (et pas existing) — la mutation
          // candidate.ical_uid portera sur le clone qui sera sauvegardé.
          const candidate = findSecondaryMatch(reservationsClone, dateStr, resolvedPlateforme, tombstones)
          if (candidate) {
            // M-3 audit : entrée ical_sync_log avec marker matched_via
            if (!Array.isArray(candidate.ical_sync_log)) candidate.ical_sync_log = []
            candidate.ical_sync_log.push({
              at: new Date().toISOString(),
              field: 'ical_uid',
              matched_via: 'csv_enriched',
              uid: uidEvent,
            })
            candidate.ical_uid = uidEvent
            // Re-indexer dans cloneByUid pour éviter un double match si le
            // même UID arrive dans un second flux iCal concurrent
            cloneByUid.set(uidEvent, candidate)
            // ADR-179 : poser superhote_id si l'événement en porte un.
            // Permet aux cycles suivants de matcher via le path superhote_id
            // (stable) au lieu de retomber sur le matching secondaire.
            if (superhoteId && !candidate.superhote_id) {
              candidate.superhote_id = superhoteId
              cloneBySuperhoteId.set(superhoteId, candidate)
            }
            // console.log informational : enrichissement attendu, pas une alerte
            console.log('[iCal sync] CSV résa enrichie via matching (checkIn, plateforme)', {
              lid, mk, dateStr, plateforme: resolvedPlateforme, uid: uidEvent, superhoteId: superhoteId || null,
            })
            updatedCount++
            continue
          }
        }

        // UID inconnu et pas de candidate → nouvelle résa fantôme avec ical_uid posé
        // Q-2 : si fall-through après garde anti-fusion, ne pas hériter du
        // même superhote_id que la résa originale (éviterait collision Map).
        const fantomShId = fellThroughShIdGuard ? null : (superhoteId || null)
        const newResa = {
          id: uid(),
          voyageur: voyageurResolved,
          plateforme: sourceOverride || e.source,
          checkIn: dateStr,
          date: dateStr,
          nuits: nuits > 0 ? nuits : 1,
          montant: 0,
          menage: 0,
          taxe: 0,
          notes: 'Import iCal',
          ical_uid: uidEvent,
          // ADR-179 : stocke superhote_id sur le fantôme pour re-matching
          // stable au prochain cycle (UID Superhote éphémère).
          superhote_id: fantomShId,
          manual_origin: false,
          ical_sync_log: [],
        }
        reservationsClone.push(newResa)
        if (fantomShId) cloneBySuperhoteId.set(fantomShId, newResa)
        added++
        continue
      }

      // ── PATH LEGACY (sans UID) ──
      // Filet B-1 : skip si date est dans tombstones legacy
      if (deletedLegacyDateSet.has(dateStr)) continue

      // Dédup classique par date
      if (existDateLegacy.has(dateStr)) continue

      reservationsClone.push({
        id: uid(),
        voyageur: voyageurResolved,
        plateforme: sourceOverride || e.source,
        checkIn: dateStr,
        date: dateStr,
        nuits: nuits > 0 ? nuits : 1,
        montant: 0,
        menage: 0,
        taxe: 0,
        notes: 'Import iCal',
        ical_uid: null,
        manual_origin: false,
        ical_sync_log: [],
      })
      added++
    }

    // saveData uniquement si ajout OU mise à jour effective
    if (added > 0 || updatedCount > 0) {
      const nd = JSON.parse(JSON.stringify(data))
      if (!nd[mk]) nd[mk] = {}
      if (!nd[mk][lid]) nd[mk][lid] = {}
      nd[mk][lid].reservations = reservationsClone
      await saveData(nd)
    }

    // Audit comptable best-effort (M-1) : si facture déjà émise sur l'ancien
    // mois, logger en error pour signaler le risque de divergence.
    for (const a of auditErrors) {
      // eslint-disable-next-line no-await-in-loop
      const hasInvoice = await _hasInvoiceForMonth(a.lid, a.oldMk)
      if (hasInvoice) {
        console.error(
          '[iCal sync] checkIn UID changed AFTER invoice emitted — audit required',
          { lid: a.lid, oldMk: a.oldMk, newMk: a.newMk, uid: a.uid, oldCheckIn: a.oldCheckIn, newCheckIn: a.newCheckIn },
        )
      }
    }

    return added
  } catch (e) {
    // Q-6 : ne pas avaler silencieusement les exceptions. Pattern anti-bugs F5.
    console.error('[importIcalForLog] exception inattendue, retourne 0 :', e)
    return 0
  }
}
